import { useEffect, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import { usePlaceByRules } from "../hooks/usePlaceByRules";
import { placeByRulesProblems, PROBLEM_KINDS } from "../util/placeByRules";
import PlaceByRulesDialog from "./PlaceByRulesDialog";
import PlaceByRulesProblems from "./PlaceByRulesProblems";

// #3567 / ADR 0004 -- the Resend dialog's "class not placed" error: what is
// wrong (class N is not placed yet), why (from the auto-placer's own decision,
// fetched here), and the fix as buttons. Only used when the server refused with
// class_not_placed; `lookupFailed` is the gate's "could not confirm" refusal,
// which has no reason to look up, only a retry.
export default function ResendClassNotPlaced({
  classId,
  classNumber,
  lookupFailed = false,
  onRetry,
  onPlaced,
  onNavigate,
}) {
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  const { preview: fetchPreview } = usePlaceByRules();
  const [state, setState] = useState({ status: lookupFailed ? "lookupFailed" : "loading", preview: null });
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (lookupFailed) return undefined;
    let cancelled = false;
    fetchPreview(classId)
      .then((preview) => !cancelled && setState({ status: "ready", preview }))
      .catch(() => !cancelled && setState({ status: "failed", preview: null }));
    return () => {
      cancelled = true;
    };
  }, [classId, lookupFailed, fetchPreview]);

  const num = classNumber ?? state.preview?.classNumber ?? "";
  const tryAgain = (
    <Button variant="outlined" size="small" onClick={onRetry} data-testid="resend-not-placed-try-again">
      {t("placeByRules.tryAgain")}
    </Button>
  );

  if (state.status === "lookupFailed" || state.status === "failed") {
    // lookupFailed: the gate could not tell if it is placed. failed: the gate said
    // NOT placed, only the follow-up "why" lookup failed, so do not say "could not check if placed".
    const words = state.status === "failed" ? "placeByRules.notPlacedCouldNotCheckWhy" : "placeByRules.couldNotCheckPlaced";
    return (
      <Alert severity="error" sx={{ mt: 2 }} data-testid="resend-not-placed-lookup-failed">
        <Stack spacing={1}>
          <Typography variant="body2">{t(words, { classNumber: num })}</Typography>
          <div>{tryAgain}</div>
        </Stack>
      </Alert>
    );
  }
  if (state.status === "loading") {
    return (
      <Alert severity="info" sx={{ mt: 2 }} data-testid="resend-not-placed-loading">
        {t("placeByRules.notPlacedYet", { classNumber: num })} {t("placeByRules.checking")}
      </Alert>
    );
  }

  const preview = state.preview;
  const alreadyPlaced = placeByRulesProblems(preview).some((p) => p.kind === PROBLEM_KINDS.ALREADY_PLACED);
  return (
    <Alert severity="error" sx={{ mt: 2 }} data-testid="resend-not-placed">
      <Stack spacing={1.5}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t(alreadyPlaced ? "placeByRules.placedNow" : "placeByRules.notPlacedYet", { classNumber: num })}
        </Typography>
        {preview.placeable ? (
          <>
            <Typography variant="body2">{t("placeByRules.everyRideScored")}</Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Button variant="contained" size="small" onClick={() => setPlacing(true)} data-testid="resend-place-by-rules">
                {t("placeByRules.placeByRules")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                data-testid="place-by-rules-open-placings"
                onClick={() => {
                  onNavigate?.();
                  navigate(
                    `/placings?classId=${encodeURIComponent(preview.classId)}` +
                      (preview.showId ? `&showId=${encodeURIComponent(preview.showId)}` : ""),
                  );
                }}
              >
                {t("placeByRules.openPlacings", { classNumber: num })}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <PlaceByRulesProblems preview={preview} classNumber={classNumber} onNavigate={onNavigate} />
            {alreadyPlaced && <div>{tryAgain}</div>}
          </>
        )}
      </Stack>
      {placing && (
        <PlaceByRulesDialog
          classId={classId}
          classNumber={classNumber}
          showId={preview.showId}
          onClose={() => setPlacing(false)}
          onNavigate={onNavigate}
          onPlaced={(result) => {
            setPlacing(false);
            onPlaced?.(result);
          }}
        />
      )}
    </Alert>
  );
}
